import React, { useState, useRef, useEffect } from 'react';
import s from './MediaPress.module.css';

/* ──────────────────────────────────────────
   Lazy YouTube embed — loads iframe only on click.
   Structure inspired by narkis-barazani/Shorts.js
   ────────────────────────────────────────── */
function LazyYoutubeEmbed({ videoId, startTime = 0, title = 'וידאו' }) {
  const [playing, setPlaying] = useState(false);

  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}` +
    `?autoplay=1&rel=0${startTime ? `&start=${startTime}` : ''}`;

  const thumbHQ = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const thumbFallback = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (playing) {
    return (
      <iframe
        className={s.videoIframe}
        src={src}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <button
      type="button"
      className={s.videoThumb}
      onClick={() => setPlaying(true)}
      aria-label={`נגן: ${title}`}
    >
      <img
        src={thumbHQ}
        alt=""
        loading="lazy"
        onError={(e) => { e.target.src = thumbFallback; }}
      />
      <span className={s.videoDark} aria-hidden="true" />
      <span className={s.videoPlay} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}

const ARTICLES = [
  {
    title: 'פרויקט השיפוץ של לירית לנגר',
    source: 'Mako · עיצוב ובנייה',
    url: 'https://www.mako.co.il/living-architecture/local-renovation/Article-83306797f62ff61027.htm',
  },
];

export default function MediaPress({ onOpenLead }) {
  const sectionRef = useRef(null);

  /* Reveal on scroll */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const targets = el.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(s.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  return (
    <section className={s.section} ref={sectionRef} id="press">
      <div className={s.inner}>
        <header className={s.header}>
          <h2 className={s.heading} data-reveal>
            מהעיתונות וממסכי <em className={s.headingEm}>הטלוויזיה</em>
          </h2>
          <p className={s.sub} data-reveal>
            הסטודיו זכה להופעות, לכתבות ולשיתופי פעולה עם גורמים
            מעולמות האדריכלות, העיצוב והתקשורת.
          </p>
        </header>

        {/* Featured video — framed architectural */}
        <figure className={s.videoFrame} data-reveal>
          <LazyYoutubeEmbed
            videoId="yVH8Y4arlw0"
            startTime={374}
            title="לירית לנגר ביוטיוב"
          />
          <figcaption className={s.videoCaption}>
            <span className={s.videoCaptionText}>
              הופעה ביוטיוב · ראיון עם לירית לנגר
            </span>
          </figcaption>
        </figure>

        {/* Articles — framed cards */}
        <div className={s.articles}>
          {ARTICLES.map((a, i) => (
            <a
              key={i}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className={s.articleCard}
              data-reveal
              style={{ '--delay': `${i * 0.08}s` }}
            >
              <div className={s.articleInner}>
                <span className={s.articleSource}>{a.source}</span>
                <h3 className={s.articleTitle}>{a.title}</h3>
                <span className={s.articleLink}>
                  <span>לקריאת הכתבה</span>
                  <span className={s.articleArrow} aria-hidden="true">←</span>
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* Dark-mode CTA — framed like the rest but inverted */}
        <button type="button" className={s.cta} onClick={onOpenLead} data-reveal>
          <span className={s.ctaText}>רוצים שהפרויקט הבא יהיה שלכם?</span>
          <span className={s.ctaArrow} aria-hidden="true">←</span>
        </button>
      </div>
    </section>
  );
}
